import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, ArrowLeft, ShieldCheck } from 'lucide-react';
import axiosInstance from '../../../lib/axios';
import { uploadRegistrationDocument } from '../../../lib/uploads';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import StudentConsentModal from '../../auth/components/StudentConsentModal';

const PasswordStrength = ({ password }) => {
  const checks = [
    { label: 'At least 8 characters', pass: password.length >= 8 },
    { label: 'One uppercase letter', pass: /[A-Z]/.test(password) },
    { label: 'One number', pass: /[0-9]/.test(password) },
  ];
  const strength = checks.filter((c) => c.pass).length;
  const colors = ['bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-500'];

  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all duration-300 ${
              i < strength ? colors[strength] : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
      <ul className="space-y-1">
        {checks.map(({ label, pass }) => (
          <li key={label} className={`flex items-center gap-1.5 text-xs ${pass ? 'text-green-600' : 'text-gray-400'}`}>
            <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              {pass
                ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                : <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              }
            </svg>
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
};

const CreatePassword = ({ registrationId, studentData, onBack }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConsent, setShowConsent] = useState(false);
  const navigate = useNavigate();
  const { login, updateUser } = useAuth();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const passwordValue = watch('password', '');

  const onSubmit = async (data) => {
    if (data.password !== data.confirmPassword) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Step 1: Create user in Auth Service
      const authRes = await axiosInstance.post('/registration/complete-registration', {
        registrationId,
        password: data.password,
      });

      const { accessToken, user, csrfToken } = authRes.data?.data || {};
      if (accessToken) {
        // Secure: store token in-memory, user metadata in localStorage
        login(accessToken, user, csrfToken);

        // Step 2: Upload Profile/License
        // The document goes straight from here to B2 and its key is submitted
        // with the profile — the file itself never passes through our backend.
        // This has to happen after login above, because the upload URL is issued
        // against the newly created account.
        if (studentData?.role === 'veterinarian' && studentData.licenseFile) {
          const key = await uploadRegistrationDocument(studentData.licenseFile, 'license');
          await axiosInstance.post('/students/profile/veterinarian-license', { key });
        } else if (studentData?.role !== 'veterinarian' && studentData) {
          // Fix 2: Wrap profile creation in its own try-catch so errors are
          // surfaced to the user instead of being swallowed silently.
          try {
            const collegeIdKey = studentData.collegeIdFile
              ? await uploadRegistrationDocument(studentData.collegeIdFile, 'collegeId')
              : undefined;

            await axiosInstance.post('/students/profile', {
              collegeId: studentData.collegeId,
              yearOfStudy: studentData.yearOfStudy,
              ...(studentData.collegeEmail ? { collegeEmail: studentData.collegeEmail } : {}),
              ...(collegeIdKey ? { collegeIdKey } : {}),
            });
          } catch (profileErr) {
            console.error('Failed to save student profile:', profileErr);
            setError(
              profileErr.response?.data?.message ||
              'Your account was created but we could not save your college details. Please contact support.'
            );
          }
        }

        // Fix 1: Fetch fresh user data and update BOTH the auth context and the
        // React Query ['me'] cache so ProfilePage sees college immediately without
        // waiting for the 4-minute staleTime to expire.
        try {
          const freshUser = await axiosInstance.get('/users/me');
          updateUser(freshUser.data.data);
          queryClient.setQueryData(['me'], freshUser.data.data);
        } catch (e) {
          console.error('Failed to fetch fresh user profile', e);
          // Non-fatal: invalidate so the cache re-fetches on next access
          queryClient.invalidateQueries({ queryKey: ['me'] });
        }
      }

      if (user?.role === 'student') {
        setShowConsent(true);
      } else {
        navigate('/feed');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      {showConsent && (
        <StudentConsentModal
          onAccepted={(updatedUser) => {
            if (updatedUser) updateUser(updatedUser);
            navigate('/dashboard');
          }}
        />
      )}
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors"
      >
        <ArrowLeft size={16} />
        Back
      </button>

      <div className="flex items-center justify-center mb-5">
        <div className="p-4 bg-green-50 rounded-2xl">
          <ShieldCheck size={32} className="text-green-500" />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-1 text-center">Create a password</h2>
      <p className="text-gray-500 mb-8 text-sm text-center">Choose a strong password for your new account</p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg text-center font-medium">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <div className="relative">
            <input
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Must be at least 8 characters' },
                pattern: { value: /^(?=.*[A-Z])(?=.*[0-9])/, message: 'Must include uppercase and number' },
                validate: {
                  noEmoji: (val) => !/\p{Extended_Pictographic}/u.test(val) || 'Emojis are not allowed in password'
                }
              })}
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              disabled={loading}
              className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
          {passwordValue && <PasswordStrength password={passwordValue} />}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
          <div className="relative">
            <input
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: {
                  matches: (val) => val === passwordValue || 'Passwords do not match',
                  noEmoji: (val) => !/\p{Extended_Pictographic}/u.test(val) || 'Emojis are not allowed in password'
                },
              })}
              type={showConfirm ? 'text' : 'password'}
              placeholder="Repeat your password"
              disabled={loading}
              className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword.message}</p>}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-6 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        >
          {loading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Creating account...</span>
            </div>
          ) : 'Create Account & Sign In'}
        </button>
      </form>
    </div>
  );
};

export default CreatePassword;
